import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";

import { useNavGroups, useNavLabel, type NavItem } from "./navConfig";
import { useCurrentPath } from "./useRouteTitle";

export interface NavSidebarProps {
  /** `rail` is the tablet posture: icons only until tapped open. */
  mode?: "full" | "rail";
  /** Touch posture: larger targets, no hover affordance. */
  touch?: boolean;
  collapsed?: boolean;
  onNavigate?: () => void;
}

const isActive = (path: string, current: string) =>
  path === "/" ? current === "/" : current === path || current.startsWith(`${path}/`);

/** The desktop and tablet navigation. A parent expands; it does not navigate. */
export function NavSidebar({ mode = "full", touch = false, onNavigate }: NavSidebarProps) {
  const groups = useNavGroups();
  const label = useNavLabel();
  const current = useCurrentPath();
  const rail = mode === "rail";

  const height = touch ? "min-h-11" : "min-h-8";
  const hover = touch ? "" : "hover:bg-accent hover:text-accent-foreground";

  const [open, setOpen] = useState<Record<string, boolean>>({});

  const leaf = (item: NavItem, depth: number) => {
    const Icon = item.icon;
    const active = isActive(item.path, current);
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onNavigate}
        title={label(item)}
        className={`type-body flex ${height} items-center gap-sm rounded-sm px-sm ${hover} ${
          active ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground"
        } ${rail ? "justify-center px-0" : depth > 0 ? "pl-xl" : ""}`}
      >
        <Icon className="size-4 shrink-0" />
        {rail ? null : <span className="truncate">{label(item)}</span>}
      </Link>
    );
  };

  return (
    <nav aria-label="Main" className="flex flex-col gap-md py-sm">
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-[2px] px-xs">
          {group.label && !rail ? (
            <div className="type-caption px-sm pb-[2px] text-muted-foreground/80 uppercase">
              {group.label}
            </div>
          ) : null}
          {group.items.map((item) => {
            if (!item.children) return leaf(item, 0);
            const Icon = item.icon;
            const expanded =
              open[item.path] ?? item.children.some((c) => isActive(c.path, current));
            if (rail) {
              return (
                <div key={item.path} className="flex flex-col gap-[2px]">
                  {item.children.map((child) => leaf(child, 0))}
                </div>
              );
            }
            return (
              <div key={item.path} className="flex flex-col gap-[2px]">
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpen((s) => ({ ...s, [item.path]: !expanded }))}
                  className={`type-body flex ${height} items-center gap-sm rounded-sm px-sm text-muted-foreground ${hover}`}
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="flex-1 text-left">{label(item)}</span>
                  {expanded ? (
                    <ChevronDown className="size-3.5" />
                  ) : (
                    <ChevronRight className="size-3.5" />
                  )}
                </button>
                {expanded ? item.children.map((child) => leaf(child, 1)) : null}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
